import { useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { Eye, EyeOff, ArrowRight, Mail } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const openForgot = () => {
    setForgotEmail(email);
    setForgotSent(false);
    setForgotOpen(true);
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email: forgotEmail.trim().toLowerCase() });
      setForgotSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao enviar. Tente novamente.");
    } finally { setForgotLoading(false); }
  };

  if (user && user !== false) {
    const to = location.state?.from?.pathname || "/dashboard";
    return <Navigate to={to} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (res.ok) {
      const to = location.state?.from?.pathname || "/dashboard";
      navigate(to, { replace: true });
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-background">
      {/* Left: brand hero */}
      <div className="hidden lg:flex relative flex-col items-center justify-center overflow-hidden"
        style={{
          background:
            "linear-gradient(135deg, #F0FAFB 0%, #FEF7EF 55%, #FDE9F1 100%)",
        }}
      >
        <div className="absolute inset-0 opacity-40" style={{
          backgroundImage:
            "radial-gradient(circle at 20% 30%, rgba(78,192,198,0.25), transparent 45%), radial-gradient(circle at 80% 70%, rgba(245,166,192,0.3), transparent 40%), radial-gradient(circle at 60% 20%, rgba(232,184,122,0.25), transparent 40%)",
        }} />
        <div className="relative z-10 flex flex-col items-center text-center px-12 max-w-lg">
          <img src="/brand/logo.webp" alt="Anjos de Patas" className="w-80 h-80 object-contain drop-shadow-sm" />
          <h1 className="mt-6 font-heading text-3xl xl:text-4xl font-bold text-brand-turquoise leading-tight">
            Cuidado que transforma vidas
          </h1>
          <p className="mt-4 text-base text-slate-700/80 leading-relaxed">
            Sistema de gestão do <span className="font-semibold text-brand-lilac">Centro de Reabilitação e Equoterapia Anjos de Patas</span>.
          </p>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-white">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex items-center gap-3">
            <img src="/brand/logo.webp" alt="Anjos de Patas" className="h-14 w-14 object-contain" />
            <div>
              <p className="font-heading font-bold text-brand-turquoise text-lg leading-tight">Anjos de Patas</p>
              <p className="text-[10px] uppercase tracking-widest text-brand-lilac font-semibold">
                Reabilitação e Equoterapia
              </p>
            </div>
          </div>

          <h2 className="font-heading text-3xl font-bold tracking-tight text-slate-900">
            Bem-vindo de volta
          </h2>
          <p className="mt-2 text-slate-500">
            Entre com sua conta para acessar o painel.
          </p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-5">
            {error && (
              <Alert variant="destructive" data-testid="login-error-alert" className="border-destructive/30 bg-destructive/5">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">E-mail</Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email" autoComplete="email" required
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="h-11 rounded-lg" placeholder="voce@anjosdepatas.com"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Senha</Label>
                <button type="button" onClick={openForgot} className="text-xs text-brand-turquoise hover:underline" data-testid="login-forgot-link">
                  Esqueci minha senha
                </button>
              </div>
              <div className="relative">
                <Input id="password" data-testid="login-password-input"
                  type={showPwd ? "text" : "password"} autoComplete="current-password" required
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  className="h-11 rounded-lg pr-11" placeholder="••••••••"
                />
                <button type="button" data-testid="login-toggle-password-btn"
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-slate-700"
                >
                  {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit" data-testid="login-submit-btn" disabled={loading}
              className="w-full h-11 rounded-lg bg-brand-turquoise hover:bg-[#3A9CA2] text-white font-semibold transition-colors"
            >
              {loading ? "Entrando…" : (<>Entrar<ArrowRight className="ml-1.5 h-4 w-4" /></>)}
            </Button>
          </form>
        </div>
      </div>

      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent className="max-w-md" data-testid="forgot-dialog">
          <DialogHeader>
            <DialogTitle>Recuperar senha</DialogTitle>
            <DialogDescription>
              Digite o e-mail cadastrado. Enviaremos um link para você criar uma nova senha (válido por 1 hora).
            </DialogDescription>
          </DialogHeader>

          {forgotSent ? (
            <div className="py-6 text-center" data-testid="forgot-sent">
              <div className="mx-auto h-14 w-14 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mb-3">
                <Mail className="h-6 w-6" />
              </div>
              <p className="text-sm text-slate-800 font-medium">Verifique sua caixa de entrada</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Se o e-mail existir no sistema, um link de recuperação foi enviado para <b>{forgotEmail}</b>.
              </p>
              <p className="mt-2 text-[11px] text-muted-foreground">Não recebeu? Verifique também a pasta de spam.</p>
              <Button className="mt-4 bg-brand-turquoise hover:bg-[#3A9CA2] text-white" onClick={() => setForgotOpen(false)} data-testid="forgot-close">
                Fechar
              </Button>
            </div>
          ) : (
            <form onSubmit={submitForgot} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="forgot-email">E-mail</Label>
                <Input id="forgot-email" data-testid="forgot-email-input" type="email" required autoFocus
                  value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="voce@email.com" className="h-11 rounded-lg" />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setForgotOpen(false)}>Cancelar</Button>
                <Button type="submit" disabled={forgotLoading} className="bg-brand-turquoise hover:bg-[#3A9CA2] text-white" data-testid="forgot-submit-btn">
                  {forgotLoading ? "Enviando…" : "Enviar link"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
